// ============================================================
// woodCalc.js
// Wood fence estimator engine.
//
// Mirrors src/utils/vinylCalc.js's structure and output contract so it is a
// drop-in for the same Estimator UI → NewDocument/EditDocument flow:
//   - export function calculateWood(specs, products, priceLevel = 'A')
//   - returns exactly { items, warnings }
//   - identical lookupSKU / getPrice / addItem semantics and line-item shape
//   - final SKU-merge pass (same-sku lines summed, sku-less lines appended last)
//
// Prices are NOT here — line prices are resolved by SKU against the Price List
// (Firestore `products`) at the given priceLevel (A/B/C).
//
// Styles: fullPrivacy | shadowbox | spacedPicket | paddock | dogEar
// Heights: '4' | '5' | '6' | '8'  (dogEar 6 only; paddock/spacedPicket 4 or 5)
//
// TAKE-OFF RULES (what is counted):
//   Sections/posts — per run: sections = ceil(feet/8); line posts = sections-1
//                    (0 when feet < 8). Regular posts = line + corners + ends;
//                    gate posts = 2 per gate. 1 cap per post (4x4 / 6x6).
//   Pickets        — non-paddock only. Feet INCLUDE gate widths. Count depends
//                    on style + board width (1x4 = 3.5", 1x6 = 5.5") and, for
//                    shadowbox/spacedPicket, the gap.
//   Framing        — rails per run: sections * (height 4 ? 2 : 3).
//   Cap / fascia   — 1 per section each. LATTICE SUPPRESSES THE CAP BOARD.
//   Lattice        — fullPrivacy 6 / shadowbox 6 only: 1 panel + 2 boards per
//                    section; picket forced to the 1x4x5.
//   Paddock        — replaces pickets/rails/cap/fascia: horizontal boards,
//                    faceboards (1 per regular post + 1 extra per corner),
//                    optional welded wire (1 roll per 100 ft incl. gates).
//   Gates          — cedar framing, hinges, latch, drop rod. Paddock gates
//                    additionally carry a FIXED picket count (15 / 30).
//
// EMISSION ORDER (what the quote reads like):
// The take-off is computed FIRST, in whatever sequence the maths needs, and the
// lines are emitted afterwards in PRESENTATION order — visible lumber, then
// structure, then hardware. This matches how chainLinkCalc ends on gate
// hardware, and it is why the per-gate loop below only ACCUMULATES: a gate
// contributes to three different groups, so it cannot emit as it goes.
//
//   Visible lumber   1 picket, 2 cap board, 3 fascia, 4 lattice panel,
//                    5 lattice board, 6 paddock board, 7 paddock faceboard,
//                    8 paddock gate picket
//   Structure        9 rail, 10 gate frame, 11 post, 12 gate post
//   Hardware        13 post cap 4x4, 14 post cap 6x6, 15 welded wire,
//                   16 hinge, 17 latch, 18 drop rod
//
// THE PICKET MUST STAY FIRST. The merge pass at the bottom collapses same-SKU
// lines onto the FIRST occurrence, keeping ITS description and unit — and
// woodSKUs.js deliberately reuses parts. 148PT is the 8' 1x4 picket AND the cap
// board AND the fascia AND the lattice board; 168PT is the 8' 1x6 picket AND the
// paddock horizontal board; 144PT/145PT/146PT are pickets AND paddock faceboards
// AND paddock gate pickets. Emitting the picket ahead of all of them is what
// keeps the surviving line reading as a picket rather than as trim. Do not move
// items 2-8 above item 1.
//
// Bad input is tolerated exactly as in vinylCalc: runs/gates default to [],
// numeric fields to 0, non-positive run lengths are ignored, and an unknown
// height/profile resolves to an undefined SKU that surfaces as PRODUCT NOT FOUND.
// ============================================================

import {
  WOOD_REGULAR_POST_SKU, WOOD_GATE_POST_SKU,
  WOOD_REGULAR_POST_CAP_SKU, WOOD_GATE_POST_CAP_SKU,
  WOOD_PICKET_1X4_SKU, WOOD_PICKET_1X6_SKU, WOOD_DOG_EAR_PICKET_SKU,
  WOOD_CAP_BOARD_SKU, WOOD_FASCIA_BOARD_SKU,
  WOOD_RAIL_SKU, WOOD_GATE_FRAME_SKU,
  WOOD_GATE_HINGE_SKU, WOOD_LATCH_SKU, WOOD_DEFAULT_LATCH_BY_HEIGHT, WOOD_DROP_ROD_SKU,
  WOOD_WELDED_WIRE_SKU, WOOD_PADDOCK_FACEBOARD_SKU, WOOD_PADDOCK_HORIZONTAL_BOARD_SKU,
  WOOD_PADDOCK_GATE_PICKET_SKU,
  WOOD_LATTICE_PANEL_SKU, WOOD_LATTICE_PICKET_SKU, WOOD_LATTICE_BOARD_SKU,
} from '../data/woodSKUs';

const lookupSKU = (sku, products) => products.find(p => p.sku === sku && p.isActive);

// Milled face width of each picket profile, in inches. Drives every picket count.
const BOARD_WIDTH_IN = { '1x4': 3.5, '1x6': 5.5 };

// Default gap (inches) between pickets when specs.spacing is not supplied.
// Shadowbox varies by profile; spaced picket is 2.5 for both.
const DEFAULT_SHADOWBOX_GAP_IN = { '1x4': 2.5, '1x6': 3.5 };
const DEFAULT_SPACED_PICKET_GAP_IN = 2.5;

// Fixed picket count per paddock gate leaf-set (single = 15, double = 30).
// The SKU itself lives in woodSKUs.js as WOOD_PADDOCK_GATE_PICKET_SKU.
const PADDOCK_GATE_PICKETS_SINGLE = 15;

export function calculateWood(specs, products, priceLevel = 'A') {
  const {
    style = 'fullPrivacy',
    height = '6',
    runs = [],
    corners = 0,
    ends = 0,
    picketProfile = '1x4',
    spacing,
    capBoard = false,
    fasciaBoard = false,
    lattice = false,
    paddockBoards = 3,
    weldedWire = false,
    hinge = '8',
    latch,
    gates = [],
  } = specs || {};

  const warnings = [];
  const items = [];

  const getPrice = (product) => {
    if (!product) return 0;
    if (priceLevel === 'B') return parseFloat(product.priceB || 0);
    if (priceLevel === 'C') return parseFloat(product.priceC || 0);
    return parseFloat(product.priceA || 0);
  };

  // Identical to vinylCalc's addItem: guards only on qty, so an undefined/missing
  // SKU still surfaces as a "PRODUCT NOT FOUND" warning line rather than
  // vanishing silently. Quantities are rounded UP here, once, for every material.
  const addItem = (sku, qty, fallbackLabel, unit = 'pc', roundable = false) => {
    if (qty <= 0) return;
    const product = lookupSKU(sku, products);
    if (!product) warnings.push(sku);
    items.push({
      id: Date.now() + Math.random(),
      sku,
      description: product ? product.description : `⚠️ PRODUCT NOT FOUND - SKU: ${sku}`,
      qty: Math.ceil(qty),
      unit: product?.unit || unit,
      unitPrice: getPrice(product),
      isStock: product?.isStock ?? true,
      productId: product?.id || null,
      found: !!product,
      roundable,
      fromEstimator: true,
      estimatorKind: 'wood',
    });
  };

  const safeRuns = Array.isArray(runs) ? runs : [];
  const safeGates = Array.isArray(gates) ? gates : [];
  const cornerCount = parseInt(corners) || 0;
  const endCount = parseInt(ends) || 0;
  const h = String(height);
  const isPaddock = style === 'paddock';

  // ── Take-off: sections, posts, rails ──
  // Per run: sections = ceil(feet/8); line posts = sections - 1, but 0 when the
  // run is under 8 ft (a short run is one section with no intermediate post).
  // Rails are accumulated here too since they are a per-run × per-section figure.
  const railsPerSection = h === '4' ? 2 : 3;
  let totalSections = 0;
  let totalLinePosts = 0;
  let totalRails = 0;
  let totalRunFeet = 0;
  safeRuns.forEach(r => {
    const feet = parseFloat(r?.feet) || 0;
    if (feet <= 0) return;
    const sectionsForRun = Math.ceil(feet / 8);
    totalRunFeet += feet;
    totalSections += sectionsForRun;
    if (feet >= 8) totalLinePosts += sectionsForRun - 1;
    totalRails += sectionsForRun * railsPerSection;
  });

  const totalGateFeet = safeGates.reduce((sum, g) => sum + (parseFloat(g?.widthFeet) || 0), 0);

  const regularPosts = totalLinePosts + cornerCount + endCount;
  const gatePosts = 2 * safeGates.length;

  // ── Pickets (every style except paddock) ──
  // Picket footage INCLUDES gate widths — gates are picketed like the fence.
  let picketSku;
  let picketCount = 0;
  if (!isPaddock) {
    // Effective profile: lattice forces 1x4; dog ear is a 6" board so it counts
    // as 1x6; heights 4 and 5 are always 1x4; 6 and 8 honour picketProfile.
    let effectiveProfile;
    if (lattice) effectiveProfile = '1x4';
    else if (style === 'dogEar') effectiveProfile = '1x6';
    else if (h === '4' || h === '5') effectiveProfile = '1x4';
    else effectiveProfile = picketProfile === '1x6' ? '1x6' : '1x4';

    // SKU: lattice forces the 1x4x5; dog ear has its own part; otherwise the
    // picket is looked up by profile + height.
    if (lattice) picketSku = WOOD_LATTICE_PICKET_SKU;
    else if (style === 'dogEar') picketSku = WOOD_DOG_EAR_PICKET_SKU;
    else picketSku = effectiveProfile === '1x6' ? WOOD_PICKET_1X6_SKU[h] : WOOD_PICKET_1X4_SKU[h];

    const boardWidth = BOARD_WIDTH_IN[effectiveProfile];
    const picketInches = (totalRunFeet + totalGateFeet) * 12;

    // Lattice keeps the STYLE's formula — only the profile and SKU change.
    if (style === 'fullPrivacy' || style === 'dogEar') {
      // Solid: boards butt together, no gap.
      picketCount = picketInches / boardWidth;
    } else if (style === 'shadowbox') {
      // Two alternating faces, so double the single-face count.
      const gap = Number.isFinite(parseFloat(spacing))
        ? parseFloat(spacing)
        : DEFAULT_SHADOWBOX_GAP_IN[effectiveProfile];
      picketCount = (picketInches / (boardWidth + gap)) * 2;
    } else if (style === 'spacedPicket') {
      const gap = Number.isFinite(parseFloat(spacing))
        ? parseFloat(spacing)
        : DEFAULT_SPACED_PICKET_GAP_IN;
      picketCount = picketInches / (boardWidth + gap);
    }
  }

  // ── Paddock ──
  // Replaces the picket/rail/cap/fascia take-off entirely.
  let paddockBoardQty = 0;
  let paddockFaceboardQty = 0;
  let weldedWireRolls = 0;
  if (isPaddock) {
    const boardsPerSection = parseInt(paddockBoards) || 0;
    paddockBoardQty = totalSections * boardsPerSection;
    // 1 faceboard per regular post, PLUS 1 extra at each corner. Gate posts get none.
    paddockFaceboardQty = regularPosts + cornerCount;
    // Welded wire: 1 roll per 100 ft, gate widths included.
    if (weldedWire) weldedWireRolls = Math.ceil((totalRunFeet + totalGateFeet) / 100);
  }

  // ── Gates ──
  // Gate posts and their caps were counted with the posts above. A gate feeds
  // THREE different presentation groups — its picket is lumber, its cedar frame
  // is structure, its hinge/latch/drop rod are hardware — so this loop cannot
  // emit as it goes.
  //
  // Each gate's contribution is staged as its own entry rather than summed here,
  // and the entries are replayed one addItem call per gate below. That keeps the
  // behaviour identical to emitting inline: the same number of calls, so the same
  // per-call Math.ceil, the same merge-pass summing, the same warning pushed once
  // per gate for a missing SKU, and the same one-line-per-gate expansion when a
  // SKU is undefined and the line falls through to noSkuItems.
  const hingeSku = WOOD_GATE_HINGE_SKU[hinge || '8'];
  const latchKey = latch || WOOD_DEFAULT_LATCH_BY_HEIGHT[h] || 'thumb';
  const latchSku = WOOD_LATCH_SKU[latchKey];
  const gateFramePerLeafSet = h === '4' ? 3 : 4;

  const gateFrameQtys = [];
  const hingeQtys = [];
  const latchQtys = [];
  const dropRodQtys = [];
  const paddockGatePicketQtys = [];

  safeGates.forEach(gate => {
    const isDouble = !!gate?.double;

    // Cedar framing: 3 pieces at 4', 4 pieces otherwise; doubled for a double gate.
    gateFrameQtys.push(gateFramePerLeafSet * (isDouble ? 2 : 1));
    // Hinges are sold per pair — 1 pair per leaf.
    hingeQtys.push(isDouble ? 2 : 1);
    // 1 latch per gate, whether single or a double pair.
    latchQtys.push(1);
    // Drop rod on double pairs only.
    if (isDouble) dropRodQtys.push(1);

    // Paddock gates are picketed with a FIXED count, separate from the fence
    // picket take-off (paddock runs have no pickets at all).
    if (isPaddock) {
      paddockGatePicketQtys.push(PADDOCK_GATE_PICKETS_SINGLE * (isDouble ? 2 : 1));
    }
  });

  // ══ EMISSION — presentation order, nothing is recalculated below this line ══

  // ── Visible lumber ──
  // The picket leads the whole document, which is also what keeps it owning the
  // shared 148PT / 168PT / 14xPT lines through the merge pass.
  if (!isPaddock) {
    addItem(picketSku, picketCount, 'Wood Picket');
    // Lattice occupies the top of the fence, so it SUPPRESSES the cap board even
    // when the flag is set. Fascia is still allowed alongside lattice.
    if (capBoard && !lattice) addItem(WOOD_CAP_BOARD_SKU, totalSections, 'Cap Board 1x4x8');
    if (fasciaBoard) addItem(WOOD_FASCIA_BOARD_SKU, totalSections, 'Fascia Board 1x4x8');
    // 1 panel + 2 boards per section. The picket was already forced to the 1x4x5.
    if (lattice) {
      addItem(WOOD_LATTICE_PANEL_SKU, totalSections, 'Lattice Panel 12x96');
      addItem(WOOD_LATTICE_BOARD_SKU, totalSections * 2, 'Lattice Board 1x4x8');
    }
  }
  if (isPaddock) {
    addItem(WOOD_PADDOCK_HORIZONTAL_BOARD_SKU, paddockBoardQty, 'Paddock Board 1x6x8');
    addItem(WOOD_PADDOCK_FACEBOARD_SKU[h], paddockFaceboardQty, 'Paddock Faceboard');
  }
  paddockGatePicketQtys.forEach(q => addItem(WOOD_PADDOCK_GATE_PICKET_SKU[h], q, 'Paddock Gate Picket'));

  // ── Structure ──
  if (!isPaddock) addItem(WOOD_RAIL_SKU, totalRails, 'Wood Rail 2x4x8');
  gateFrameQtys.forEach(q => addItem(WOOD_GATE_FRAME_SKU, q, 'Gate Frame 2x4x8 Cedar'));
  addItem(WOOD_REGULAR_POST_SKU[h], regularPosts, 'Wood Post');
  addItem(WOOD_GATE_POST_SKU[h], gatePosts, 'Wood Gate Post');

  // ── Hardware ──
  // 1 cap per post — 4x4 caps on regular posts, 6x6 caps on gate posts.
  addItem(WOOD_REGULAR_POST_CAP_SKU, regularPosts, 'Post Cap 4x4');
  addItem(WOOD_GATE_POST_CAP_SKU, gatePosts, 'Post Cap 6x6');
  if (isPaddock && weldedWire) addItem(WOOD_WELDED_WIRE_SKU[h], weldedWireRolls, 'Welded Wire');
  hingeQtys.forEach(q => addItem(hingeSku, q, 'T-Hinge (pair)'));
  latchQtys.forEach(q => addItem(latchSku, q, 'Gate Latch'));
  dropRodQtys.forEach(q => addItem(WOOD_DROP_ROD_SKU, q, 'Drop Rod / Cane Bolt'));

  // Merge items with the same SKU into one line (qtys summed, first occurrence's
  // other fields kept). Sku-less lines (undefined sku or '—') are appended last.
  const skuMap = new Map();
  const noSkuItems = [];
  items.forEach(item => {
    if (!item.sku || item.sku === '—') { noSkuItems.push(item); return; }
    if (skuMap.has(item.sku)) {
      skuMap.get(item.sku).qty += item.qty;
    } else {
      skuMap.set(item.sku, { ...item });
    }
  });

  return { items: [...skuMap.values(), ...noSkuItems], warnings };
}
